import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listAgentTransferDestinations } from "../api/transferDestinations";
import {
  cancelWarmTransfer,
  getWarmTransfer,
  leaveWarmTransfer,
  mergeWarmTransfer,
  startWarmTransfer,
  type WarmTransfer,
} from "../api/warmTransfer";

const POLL_MS = 2000;

/**
 * Shape-generic over FastAPI's {detail}/{error} bodies — same job as
 * transferDestinations.ts's `transferDestinationErrorMessage`, kept local
 * rather than imported so this module has no reason to reach into an
 * unrelated feature's API file. app/routers/agent.py's warm-transfer
 * endpoints raise `HTTPException(409, detail=...)` with copy already written
 * for the agent ("Can't reach Acme Buyers right now.", "That transfer isn't
 * waiting to be merged.") — never a raw "Request failed with status code
 * 409", which is all a bare `err.message` on an AxiosError would give us.
 */
function friendlyError(err: unknown): string {
  // axios.isAxiosError, not `instanceof AxiosError` — a runtime shape check
  // (mirrors client.test.ts's own mocked-error convention) rather than a
  // prototype check, so this reads the friendly `detail` FastAPI already
  // wrote regardless of how the rejection got here.
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as { detail?: string; error?: string } | undefined;
    return body?.detail || body?.error || "Something went wrong. Please try again.";
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}

/**
 * Server reasons -> what the agent is told once a session has genuinely
 * ENDED. The API sends a machine token so the two sides can never drift on
 * message text (the same discipline as softphone.ts's exported error
 * constants). See `buyerLeftConference` below for the same
 * "buyer_no_answer" token surfacing mid-session, worded differently there —
 * that's a live consult recovering, not an ended one.
 */
function endedNotice(reason: string, buyer: string): string | null {
  switch (reason) {
    case "buyer_no_answer":
      return `No answer from ${buyer} — you're back with the customer.`;
    case "customer_gone":
      // Says WHO is left, not just who went. After the customer drops out of
      // a consult or a conference the agent is still bridged to the buyer,
      // and the console otherwise looks like an ordinary call: the speed
      // dial comes back and nothing says the person on the line is not the
      // customer. Pressing a buyer at that point is what put two buyers on a
      // call with each other (2026-09-18, ref. 347-476-0255). The server now
      // refuses that press too; this is the line that stops it being made.
      return `The customer hung up. Anyone still on your line is ${buyer}, not the customer — hang up when you're done.`;
    case "no_route":
      return `Can't reach ${buyer} right now.`;
    case "pbx_unavailable":
      return "The phone system didn't respond. Nothing was changed.";
    default:
      return null;
  }
}

export interface WarmTransferPanelProps {
  /** Only meaningful mid-call; renders nothing otherwise. */
  onCall: boolean;
  /**
   * The seat these controls act on. An operator on the shared login has no
   * seat in their token, so every warm-transfer call must name one or the
   * backend 422s (beads u0np). AgentConsole already knows it.
   */
  extensionId: number;
  /**
   * Whether the agent has opened the Transfer disclosure.
   *
   * Gates the DESTINATION PICKER only — never a running transfer. This panel
   * used to mount its buyer buttons for the whole call, which put a row of
   * pills that dial a third party permanently on screen, directly above the
   * consent panel's identically-styled pills. An agent who opened Consent and
   * reached for a button started a consult leg instead of sending a text
   * (reported from production, 2026-09-10). The picker is now something the
   * agent asks for; a live consult/conference still shows itself, because
   * hiding an in-progress transfer behind a toggle is the worse failure.
   */
  pickerOpen?: boolean;
}

/**
 * The warm-transfer flow: hold -> private consult -> three-way conference -> leave.
 *
 * Two things this deliberately does NOT do:
 *
 *  - It never combines "bring the customer in" with "leave". The conference is
 *    a real stopping point where the agent introduces the two parties and
 *    confirms the buyer is ready; collapsing it into one button would make
 *    every transfer a blind one with extra steps. Per the plan's state table
 *    (docs/superpowers/plans/2026-07-31-warm-transfer.md), Merge and Leave are
 *    two buttons, never one, and the API has no endpoint that does both.
 *  - It never claims a stage the server has not confirmed. Every label comes
 *    from the polled session, which the API itself reconciles against
 *    Asterisk on each request (warm_transfer.py's `refresh`) — an optimistic
 *    local state here would mean an agent talking to the wrong party.
 */
export function WarmTransferPanel({ onCall, pickerOpen = false, extensionId }: WarmTransferPanelProps) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data: destinations, isError: destinationsFailed, error: destinationsError } = useQuery({
    queryKey: ["agent-transfer-destinations"],
    queryFn: listAgentTransferDestinations,
    enabled: onCall,
    staleTime: 300_000,
  });

  const { data: session } = useQuery({
    queryKey: ["warm-transfer"],
    queryFn: () => getWarmTransfer(extensionId),
    enabled: onCall,
    refetchInterval: onCall ? POLL_MS : false,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["warm-transfer"] });
  const onError = (err: unknown) => setError(friendlyError(err));

  /**
   * Which buyers have already failed to answer on THIS call.
   *
   * Derived by watching the polled session rather than from the click that
   * started it: the agent can back out from the call bar, the buyer can hang
   * up mid-conference, or the leg can simply time out, and only the session
   * knows which of those happened. Whenever a consult ends without ever
   * reaching a conference, the buyer it was calling gets marked.
   *
   * Deliberately per-mount state, not persisted: it describes this call, and
   * a name that did not answer twenty minutes ago tells the next call nothing.
   */
  const [missed, setMissed] = useState<number[]>([]);
  const inFlight = useRef<number | null>(null);
  useEffect(() => {
    const state = session?.state;
    if (state === "consulting") {
      inFlight.current = session?.destination_id ?? null;
      return;
    }
    // Reached a conference: it answered, so it is not a miss no matter what
    // happens afterwards.
    if (state === "conference" || state === "agent_left") {
      inFlight.current = null;
      return;
    }
    const failed = inFlight.current;
    if (failed == null) return;
    inFlight.current = null;
    setMissed((prev) => (prev.includes(failed) ? prev : [...prev, failed]));
  }, [session?.state, session?.destination_id]);

  const nextPickId = (destinations ?? []).find(
    (d) => !missed.includes(d.transfer_destination_id),
  )?.transfer_destination_id;

  /**
   * What the last poll to see a transfer END said, kept until the call ends.
   *
   * `refresh()` reports `ended` exactly once — the poll after it finds no
   * session and returns null — so without this the notice was a two-second
   * flash. For customer_gone that flash was the only thing telling the agent
   * that whoever is still on their line is the BUYER, and the picker came
   * straight back underneath it. Cleared by a new live session (it is about
   * the previous one) and by the call ending (the panel unmounts then anyway).
   */
  const [lastEnded, setLastEnded] = useState<string | null>(null);
  useEffect(() => {
    if (!onCall) {
      setLastEnded(null);
      return;
    }
    if (session?.state === "ended") {
      setLastEnded(endedNotice(session.message, session.destination_name) ?? "Call ended.");
    } else if (session) {
      setLastEnded(null);
    }
  }, [onCall, session]);

  const start = useMutation({
    mutationFn: (id: number) => startWarmTransfer(id, extensionId),
    onSuccess: (started) => {
      setError(null);
      // The response IS the new session. Writing it into the shared cache
      // flips this panel (and the call bar, same key) to the live stage in the
      // same tick the request resolves, instead of leaving the speed dial on
      // screen — buttons enabled again, `isPending` false — for the round
      // trip until the next poll notices. That gap is where a second press
      // went out as a second request.
      qc.setQueryData(["warm-transfer"], started);
      void invalidate();
    },
    onError,
  });
  const merge = useMutation({
    mutationFn: () => mergeWarmTransfer(extensionId),
    onSuccess: () => { setError(null); void invalidate(); },
    onError,
  });
  const cancel = useMutation({
    mutationFn: () => cancelWarmTransfer(extensionId),
    onSuccess: () => { setError(null); void invalidate(); },
    onError,
  });
  const leave = useMutation({
    mutationFn: () => leaveWarmTransfer(extensionId),
    onSuccess: () => { setError(null); void invalidate(); },
    onError,
  });

  if (!onCall) return null;

  const live: WarmTransfer | null = session ?? null;
  const buyer = live?.destination_name ?? "the buyer";
  const busy = start.isPending || merge.isPending || cancel.isPending || leave.isPending;

  // The mid-conference buyer-hangup case is reported as a *consulting*
  // session with no live buyer (buyer_up=false, message="buyer_no_answer") —
  // see warm_transfer.py's `refresh()`: the agent is still in the room with
  // the customer, nothing is currently ringing, and nobody is being called.
  // Treated the same as "no transfer running" so the destination buttons
  // come back, per the plan's edge-case table, with copy that matches what
  // just happened rather than the ordinary ringing silence.
  const buyerLeftConference =
    live?.state === "consulting" && !live.buyer_up && live.message === "buyer_no_answer";

  const idle = !live || live.state === "ended" || buyerLeftConference;

  // --- Idle: destination buttons, sometimes re-offered with an explanation -
  if (idle) {
    const notice = buyerLeftConference
      ? `${buyer} hung up — you're back with the customer.`
      : live && live.state === "ended"
        ? (endedNotice(live.message, buyer) ?? "Call ended.")
        : lastEnded;

    // Nothing running and nothing to report: show the picker only if the
    // agent actually asked for it. A notice always wins — it is feedback
    // about something that just happened to THIS call, and swallowing it
    // because a disclosure happens to be shut would leave the agent
    // wondering where the buyer went.
    if (!pickerOpen && !notice) return null;

    return (
      <div className="mt-4 rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">
          Speed dial
        </div>
        {notice && (
          <p className="mb-3 text-sm text-amber-300" role="status">
            {notice}
          </p>
        )}
        {destinationsFailed ? (
          // Everything this panel offers — hold, consult, conference, leave —
          // hangs off a destination button, so a failed list silently removes
          // the whole flow. Say so, rather than let it read as "you never set
          // any up": the fixes are completely different, and the agent is on a
          // live call while they read this.
          <p className="text-sm text-red-300" role="alert">
            Couldn't load your transfer destinations — {friendlyError(destinationsError)}
          </p>
        ) : (destinations ?? []).length === 0 ? (
          <p className="text-sm text-white/40">
            Your account has no transfer destinations set up yet.
          </p>
        ) : (
          <div className="flex flex-col gap-1">
            {(destinations ?? []).map((d) => {
              const isMissed = missed.includes(d.transfer_destination_id);
              // "Priya is highlighted as the next likely pick" — the first
              // name that has not already failed on this call. Presentational
              // only: nothing stops a retry of a missed name, it is just no
              // longer the default.
              const isNext = !isMissed && d.transfer_destination_id === nextPickId;
              return (
                <button
                  key={d.transfer_destination_id}
                  disabled={busy}
                  onClick={() => start.mutate(d.transfer_destination_id)}
                  className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${
                    isNext
                      ? "bg-amber-400/12 ring-1 ring-amber-400/40 hover:bg-amber-400/20"
                      : "hover:bg-white/10"
                  }`}
                >
                  <span className="min-w-0">
                    <span
                      className={`block truncate text-sm font-medium ${
                        isMissed ? "text-white/40 line-through" : "text-white"
                      }`}
                    >
                      {d.name}
                    </span>
                    <span className={`block truncate text-xs ${isMissed ? "text-white/25" : "text-white/45"}`}>
                      {d.routing === "sip" ? "Direct line" : "Rings out"}
                    </span>
                  </span>
                  {/* The action word sits right-aligned and reads as the state
                      of that name, per the frames: Dial / Missed / Try next. */}
                  <span
                    className={`shrink-0 text-[10px] font-semibold uppercase tracking-wider ${
                      isMissed ? "text-white/30" : isNext ? "text-amber-300" : "text-white/50"
                    }`}
                  >
                    {isMissed ? "Missed" : isNext ? "Try next" : "Dial"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <p className="mt-2 text-xs text-white/40">
          The customer stays on hold while you speak to the buyer. You choose
          when to merge them in, and when to leave.
        </p>
        {error && (
          <p className="mt-2 text-sm text-red-300" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  // --- Live session: one honest headline, straight off the polled state ----
  // Verbatim per the plan's state table — this IS the agent-facing wording.
  const headline =
    live.state === "consulting"
      ? live.buyer_up
        ? `Customer on hold · Talking to ${buyer} privately`
        : `Customer on hold · Calling ${buyer}…`
      : live.state === "conference"
        ? `All three connected — you, the customer and ${buyer}`
        : `You've left. The customer is with ${buyer}.`; // agent_left

  // The buyer as a party on the call, not a sentence about one. The approved
  // frames give them their own row — name, what they are doing, and a state
  // chip — because from state 3 onward there are genuinely two people the
  // agent is tracking, and one line of prose makes the agent parse a sentence
  // to answer "is he there yet?".
  const buyerState =
    live.state === "consulting"
      ? live.buyer_up
        ? { sub: "Speed dial · answered", chip: "Live", tone: "bg-sky-400/15 text-sky-200" }
        : { sub: "Ringing · no answer yet", chip: "Ringing", tone: "bg-violet-400/15 text-violet-200" }
      : live.state === "conference"
        ? { sub: "In conversation with the customer", chip: "Live", tone: "bg-sky-400/15 text-sky-200" }
        : { sub: "Still with the customer", chip: "Live", tone: "bg-sky-400/15 text-sky-200" };

  // Recording consent. The conference is recorded from the moment the customer
  // is put on hold, but the agent's qualifying conversation happens earlier,
  // inside Queue(), which is NOT recorded -- so a consent given back there is
  // not in the file. One spoken line once all three are connected puts it in
  // the recording that actually exists. Shown only in `conference`: before the
  // merge the customer cannot hear the agent, and after `agent_left` it is too
  // late to say it.
  const askConsent = live.state === "conference";

  return (
    <div className="mt-4 rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
      {askConsent && (
        <div
          className="mb-3 rounded-lg bg-amber-400/10 px-3 py-2 text-sm text-amber-100 ring-1 ring-amber-300/25"
        >
          <span className="font-medium">Say this now:</span>{" "}
          &ldquo;Just to confirm — you&rsquo;re both happy for this call to be
          recorded?&rdquo;
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-semibold text-white">{headline}</p>
        <span className="shrink-0 font-mono text-sm tabular-nums text-white/50">
          {Math.floor(live.seconds_elapsed / 60)}:
          {String(live.seconds_elapsed % 60).padStart(2, "0")}
        </span>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-white/5 px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-white">{buyer}</div>
          <div className="truncate text-xs text-white/50">{buyerState.sub}</div>
        </div>
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${buyerState.tone}`}
        >
          {buyerState.chip}
        </span>
      </div>

      {/* No action buttons live here any more.
          Merge, Back to speed dial and Hang up & leave call are all IN THE
          CALL BAR (AgentConsole), which is where the approved flow puts them
          and, more importantly, the only place they can be without existing
          twice. A second Merge down here next to the speed-dial pills is how
          an agent ends up pressing something that dials a third party when
          they meant something else — the defect this panel already caused
          once. This block is now status only: what is happening, and how long
          it has been happening. */}
      {live.state === "consulting" && (
        <p className="mt-2 text-xs text-white/40">
          {live.buyer_up
            ? `Brief ${buyer} before merging — they only hear you until you press Merge.`
            : `Ringing ${buyer}. Merge lights up when they answer.`}
        </p>
      )}
      {live.state === "conference" && (
        <p className="mt-2 text-xs text-white/40">
          Introduce them first. Once you leave, the customer stays with {buyer}.
        </p>
      )}

      {error && (
        <p className="mt-3 text-sm text-red-300" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
